import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import { firebaseReady, waitForObserverAuth } from "../services/firebaseAuth";
import {
  deleteResearchItem,
  loadResearchLibrary,
  ResearchLibrary,
  saveResearchItem,
} from "../services/researchLibrary";

export default function ResearchLibraryPage() {
  const [library, setLibrary] = useState<ResearchLibrary>();
  const [drafts, setDrafts] = useState<Record<string, { note: string; collection: string }>>({});
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const reload = async () => {
    const next = await loadResearchLibrary();
    setLibrary(next);
    setDrafts(Object.fromEntries(next.items.map((item) => [item.entityId, { note: item.note || "", collection: item.collection }] )));
  };

  useEffect(() => {
    if (!firebaseReady) {
      navigate(`/sign-in?returnTo=${encodeURIComponent("/library")}`, { replace: true });
      return;
    }
    void waitForObserverAuth().then((user) => {
      if (!user) {
        navigate(`/sign-in?returnTo=${encodeURIComponent("/library")}`, { replace: true });
        return;
      }
      return reload().catch((reason) => setNotice((reason as Error).message));
    });
  }, [navigate]);

  const grouped = useMemo(() => {
    const result: Record<string, NonNullable<ResearchLibrary["items"]>> = {};
    for (const item of library?.items || []) (result[item.collection || "Saved"] ??= []).push(item);
    return result;
  }, [library]);

  const updateDraft = (entityId: string, key: "note" | "collection", value: string) =>
    setDrafts((current) => ({ ...current, [entityId]: { ...current[entityId], [key]: value } }));

  if (!library)
    return <div className="ot-page"><p>{notice || "Loading your private Library…"}</p></div>;

  return (
    <div className="ot-page">
      <SeoHead title="Private Research Library | Observer Theory" description="Your private saved practitioner records and research notes." path="/library" noIndex />
      <header className="ot-page-head">
        <span className="section-kicker">Private to your account</span>
        <h1>Your research Library</h1>
        <p>
          Saved profiles and notes are account-scoped and never influence public ranking,
          reviews, moderation, or profile visibility.
        </p>
        <p><b>{library.tier === "free" ? `Free · ${library.items.length}/${library.freeLimit} profiles` : `${library.tier === "pro" ? "Pro" : "Plus"} · unlimited profiles`}</b></p>
      </header>
      {notice && <p role="status">{notice}</p>}
      {!library.items.length && <p>No profiles saved yet. <Link to="/search">Find a practitioner to research.</Link></p>}
      {Object.entries(grouped).map(([collection, items]) => (
        <section key={collection} style={{ margin: "35px 0" }}>
          <h2>{collection}</h2>
          <div style={{ display: "grid", gap: 16 }}>
            {items.map((item) => {
              const draft = drafts[item.entityId] || { note: "", collection: "Saved" };
              return (
                <article key={item.entityId} className="ot-card" style={{ padding: 20 }}>
                  <h3><Link to={`/profile/${item.publicId}`}>{item.displayName}</Link></h3>
                  {library.tier === "pro" && (
                    <label>Collection
                      <input value={draft.collection} maxLength={50} onChange={(event) => updateDraft(item.entityId, "collection", event.target.value)} />
                    </label>
                  )}
                  <label>Private note
                    <textarea value={draft.note} maxLength={library.tier === "pro" ? 5000 : library.tier === "plus" ? 2000 : 500} rows={4} onChange={(event) => updateDraft(item.entityId, "note", event.target.value)} />
                  </label>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="button button-ink" onClick={async () => { try { await saveResearchItem(item.entityId, draft.note, draft.collection); setNotice("Private Library updated."); await reload(); } catch (reason) { setNotice((reason as Error).message); } }}>Save note</button>
                    <button className="button" onClick={async () => { try { await deleteResearchItem(item.entityId); setNotice("Profile removed from your private Library."); await reload(); } catch (reason) { setNotice((reason as Error).message); } }}>Remove</button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
      {library.tier === "free" && <p><Link to="/pricing">Plus removes the ten-profile limit and expands private notes. Pro adds named collections and longer notes.</Link></p>}
    </div>
  );
}
